import React, { useEffect, useState } from "react";
import Logger from "../../../services/Logger";
import { Book as BookIcon, FilterList as FilterIcon, Person as PersonIcon, Search as SearchIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import OpenLibraryService from "./OpenLibraryService";

const OpenLibraryMain = () => {
    const [query, setQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalResults, setTotalResults] = useState(0);
    const [searchData, setSearchData] = useState(null);
    const [childrensBooks, setChildrensBooks] = useState([]);
    const [childrensLoading, setChildrensLoading] = useState(false);
    const [childrensQuery, setChildrensQuery] = useState("");
    const [filterTextContent, setFilterTextContent] = useState(false);
    const [filteringTextContent, setFilteringTextContent] = useState(false);
    const [childrensPage, setChildrensPage] = useState(1);
    const [childrensPageSize] = useState(12);
    const [sortBy, setSortBy] = useState('new');

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize from URL parameters
    useEffect(() => {
        const urlQuery = searchParams.get('q');
        const urlPage = searchParams.get('page');

        if (urlQuery) {
            setQuery(urlQuery);
        }
        if (urlPage && !isNaN(parseInt(urlPage))) {
            setCurrentPage(parseInt(urlPage));
        }

        // Auto-search if query is in URL
        if (urlQuery && urlQuery.trim()) {
            const page = urlPage && !isNaN(parseInt(urlPage)) ? parseInt(urlPage) : 1;
            fetchBooks(page);
        }

        // Load children's books on mount
        fetchChildrensBooks();
    }, [searchParams]);

    // Update URL when search parameters change
    const updateURL = (searchQuery, page) => {
        const params = new URLSearchParams();
        if (searchQuery) params.set('q', searchQuery);
        if (Number(page) > 1) params.set('page', Number(page).toString());
        
        navigate(`?${params.toString()}`, { replace: true });
    };


    const fetchChildrensBooks = async (searchQuery = "", page = childrensPage) => {
        setChildrensLoading(true);
        try {
            let result;
            if (searchQuery.trim()) {
                // Search for children's books with specific query
                result = await OpenLibraryService.searchBooksAdvanced({
                    subject: "Children's books",
                    title: searchQuery,
                    limit: childrensPageSize,
                    page: page,
                    sort: sortBy
                });
                setChildrensBooks(result.docs || []);
            } else {
                // Get popular children's books by subject
                result = await OpenLibraryService.getSubjectBooks("Children's books", page, childrensPageSize);
                setChildrensBooks(result.works || []);
            }
            setChildrensPage(page);
        } catch (err) {
            Logger.error("Children's books API Error:", err);
            // Fallback to general children's book search
            try {
                const fallbackResult = await OpenLibraryService.searchBooks("children", page, childrensPageSize);
                setChildrensBooks(fallbackResult.docs || []);
                setChildrensPage(page);
            } catch (fallbackErr) {
                Logger.error("Fallback children's books search failed:", fallbackErr);
            }
        } finally {
            setChildrensLoading(false);
        }
    };

    const fetchBooks = async (page = currentPage) => {
        if (!query.trim()) {
            setError("Please enter a search query");
            return;
        }

        setLoading(true);
        setError(null);
        setSearchResults([]);

        try {
            const numericPage = Number(page) || 1;

            // Update URL with current search parameters
            updateURL(query.trim(), numericPage);

            // Search for books using service
            const searchData = await OpenLibraryService.searchBooks(query.trim(), numericPage, pageSize, sortBy);

            if (!searchData.docs || searchData.docs.length === 0) {
                throw new Error("No books found for this query");
            }

            let booksToShow = searchData.docs;

            // Filter for text content if enabled
            if (filterTextContent) {
                setFilteringTextContent(true);
                try {
                    // First try the faster availability-based filtering
                    booksToShow = OpenLibraryService.filterBooksByAvailability(searchData.docs);
                    
                    // If no books found with availability data, fall back to individual checking
                    if (booksToShow.length === 0) {
                        booksToShow = await OpenLibraryService.filterBooksWithTextContent(searchData.docs);
                    }
                    
                    if (booksToShow.length === 0) {
                        setError("No books with readable text content found for this query. Try disabling the text content filter, searching for different terms, or check Project Gutenberg for free public domain books.");
                    }
                } catch (filterErr) {
                    Logger.error("Text content filtering error:", filterErr);
                    setError("Failed to filter books with text content. Showing all results instead.");
                    booksToShow = searchData.docs;
                } finally {
                    setFilteringTextContent(false);
                }
            }

            // Store search results and pagination info
            setSearchResults(booksToShow);
            setSearchData(searchData);
            setTotalResults(filterTextContent ? booksToShow.length : searchData.numFound || searchData.docs.length);
            setCurrentPage(numericPage);
        } catch (err) {
            Logger.error("Open Library API Error:", err);
            setError(`Failed to search books: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };


    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            fetchBooks();
        }
    };

    const handleChildrensSearch = () => {
        fetchChildrensBooks(childrensQuery);
    };

    const handleChildrensKeyPress = (event) => {
        if (event.key === "Enter") {
            handleChildrensSearch();
        }
    };

    const handleTextContentFilterChange = (event) => {
        setFilterTextContent(event.target.checked);
        // Re-search with the new filter setting if we have search results
        if (searchResults.length > 0) {
            fetchBooks();
        }
    };


    const handleChildrensPageChange = (event, page) => {
        const numericPage = Number(page);
        setChildrensPage(numericPage);
        fetchChildrensBooks(childrensQuery, numericPage);
    };

    const handleSortChange = (event) => {
        const newSort = event.target.value;
        setSortBy(newSort);
        // Re-search with the new sort setting if we have search results
        if (searchResults.length > 0) {
            fetchBooks();
        }
        // Also update children's books if they're loaded
        if (childrensBooks.length > 0) {
            fetchChildrensBooks(childrensQuery, childrensPage);
        }
    };

    const clearSearch = () => {
        setQuery("");
        setSearchResults([]);
        setError(null);
        setSearchData(null);
        setCurrentPage(1);
        setTotalResults(0);
        setFilterTextContent(false);
        navigate("/admin/system/integrate/openlibrary", { replace: true });
    };

    const handlePageChange = (event, page) => {
        const numericPage = Number(page);
        setCurrentPage(numericPage);
        fetchBooks(numericPage);
    };

    const handlePageSizeChange = (event) => {
        const newPageSize = Number(event.target.value);
        setPageSize(newPageSize);
        setCurrentPage(1);
        fetchBooks(1);
    };

    const formatAuthors = (authors) => {
        if (!authors || authors.length === 0) return "Unknown Author";
        return authors.map(author => author.name || author).join(", ");
    };

    const formatPublishYear = (publishYear) => {
        if (!publishYear || publishYear.length === 0) return "Unknown Year";
        return Array.isArray(publishYear) ? publishYear[0] : publishYear;
    };

    const formatSubjects = (subjects) => {
        if (!subjects || subjects.length === 0) return [];
        return subjects.slice(0, 5); // Limit to 5 subjects
    };

    return (
        <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 4, mb: 4, px: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Open Library API
            </Typography>

            <Grid container spacing={3}>

                {/* Children's Books Section */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            🧸 Children's Books
                        </Typography>
                        
                        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Search children's books"
                                value={childrensQuery}
                                onChange={(e) => setChildrensQuery(e.target.value)}
                                onKeyPress={handleChildrensKeyPress}
                                disabled={childrensLoading}
                                placeholder="Enter book title, character, or theme"
                            />
                            <FormControl sx={{ minWidth: 150 }}>
                                <InputLabel>Sort by</InputLabel>
                                <Select
                                    value={sortBy}
                                    onChange={handleSortChange}
                                    disabled={childrensLoading}
                                    label="Sort by"
                                >
                                    <MenuItem value="new">Newest First</MenuItem>
                                    <MenuItem value="relevance">Relevance</MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                onClick={handleChildrensSearch}
                                disabled={childrensLoading}
                                startIcon={childrensLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                                sx={{ minWidth: 120 }}
                            >
                                {childrensLoading ? "Searching..." : "Search"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setChildrensQuery("");
                                    setChildrensPage(1);
                                    fetchChildrensBooks("", 1);
                                }}
                                disabled={childrensLoading}
                            >
                                Show All
                            </Button>
                        </Box>
                        
                        {childrensLoading && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading children's books...
                                </Typography>
                            </Box>
                        )}

                        {childrensBooks.length > 0 && !childrensLoading && (
                            <>
                                <Grid container spacing={2}>
                                    {childrensBooks.map((book, index) => (
                                        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                                            <Card 
                                                sx={{ 
                                                    cursor: "pointer", 
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: 3
                                                    }
                                                }}
                                                onClick={() => navigate(`/admin/system/integrate/openlibrary/detail${book.key}`)}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    height="200"
                                                    image={OpenLibraryService.getBookCover(book.cover_id || book.cover_i)}
                                                    alt={book.title}
                                                    sx={{ objectFit: "cover" }}
                                                />
                                                <CardContent>
                                                    <Typography variant="h6" component="h3" noWrap>
                                                        {book.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {formatAuthors(book.author_name)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatPublishYear(book.first_publish_year)}
                                                    </Typography>
                                                    {book.subject && book.subject.some(subject => 
                                                        subject.toLowerCase().includes('children') || 
                                                        subject.toLowerCase().includes('juvenile')
                                                    ) && (
                                                        <Chip 
                                                            label="Children's" 
                                                            size="small" 
                                                            color="primary" 
                                                            sx={{ mt: 1 }}
                                                        />
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                                
                                {/* Children's Books Pagination */}
                                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                                    <Pagination
                                        count={Math.ceil(100 / childrensPageSize)} // Assuming we can show up to 100 children's books
                                        page={childrensPage}
                                        onChange={handleChildrensPageChange}
                                        color="primary"
                                        disabled={childrensLoading}
                                        size="small"
                                    />
                                </Box>
                            </>
                        )}
                    </Paper>
                </Grid>

                {/* Alternative Sources Info */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, mb: 3, backgroundColor: "#e3f2fd" }}>
                        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            📚 Free Book Sources
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Looking for books with readable content? Here are the best free sources:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                            <Link href="https://www.gutenberg.org/" target="_blank" rel="noopener noreferrer">
                                <Button variant="contained" size="small" startIcon={<BookIcon />}>
                                    Project Gutenberg
                                </Button>
                            </Link>
                            <Link href="https://archive.org/details/texts" target="_blank" rel="noopener noreferrer">
                                <Button variant="contained" size="small" startIcon={<BookIcon />}>
                                    Internet Archive
                                </Button>
                            </Link>
                            <Link href="https://www.hathitrust.org/" target="_blank" rel="noopener noreferrer">
                                <Button variant="contained" size="small" startIcon={<BookIcon />}>
                                    HathiTrust
                                </Button>
                            </Link>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            These sources offer millions of free books with full text content available for reading online or download.
                        </Typography>
                    </Paper>
                </Grid>

                {/* Search Section */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Book Search
                        </Typography>

                        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Search for books"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={loading || filteringTextContent}
                                placeholder="Enter book title, author, or subject"
                            />
                            <FormControl sx={{ minWidth: 150 }}>
                                <InputLabel>Sort by</InputLabel>
                                <Select
                                    value={sortBy}
                                    onChange={handleSortChange}
                                    disabled={loading || filteringTextContent}
                                    label="Sort by"
                                >
                                    <MenuItem value="new">Newest First</MenuItem>
                                    <MenuItem value="relevance">Relevance</MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                onClick={() => fetchBooks()}
                                disabled={loading || filteringTextContent || !query.trim()}
                                startIcon={loading || filteringTextContent ? <CircularProgress size={20} /> : <SearchIcon />}
                                sx={{ minWidth: 120 }}
                            >
                                {loading || filteringTextContent ? "Searching..." : "Search"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={clearSearch}
                                disabled={loading || filteringTextContent}
                            >
                                Clear
                            </Button>
                        </Box>

                        {/* Text Content Filter */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                            <FilterIcon color="primary" />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filterTextContent}
                                        onChange={handleTextContentFilterChange}
                                        disabled={loading || filteringTextContent}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight="medium">
                                            Show only books with readable text content
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            This will filter results to show only books that have full text available in Open Library. For more free books, try Project Gutenberg (gutenberg.org) which offers over 70,000 free public domain books.
                                        </Typography>
                                    </Box>
                                }
                            />
                            {filteringTextContent && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="caption" color="text.secondary">
                                        Checking text availability...
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                                {error.includes("No books with readable text content") && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                                            Alternative sources for free book content:
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                            <Link href="https://www.gutenberg.org/" target="_blank" rel="noopener noreferrer">
                                                <Button variant="outlined" size="small" startIcon={<BookIcon />}>
                                                    Project Gutenberg - 70,000+ free books
                                                </Button>
                                            </Link>
                                            <Link href="https://archive.org/details/texts" target="_blank" rel="noopener noreferrer">
                                                <Button variant="outlined" size="small" startIcon={<BookIcon />}>
                                                    Internet Archive - Millions of texts
                                                </Button>
                                            </Link>
                                            <Link href="https://www.hathitrust.org/" target="_blank" rel="noopener noreferrer">
                                                <Button variant="outlined" size="small" startIcon={<BookIcon />}>
                                                    HathiTrust - Academic library texts
                                                </Button>
                                            </Link>
                                        </Box>
                                    </Box>
                                )}
                            </Alert>
                        )}

                        {/* Search Results */}
                        {searchResults.length > 0 && (
                            <Card sx={{ mt: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6">
                                                Search Results ({totalResults.toLocaleString()} total)
                                            </Typography>
                                            {filterTextContent && (
                                                <Typography variant="caption" color="primary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <FilterIcon sx={{ fontSize: 14 }} />
                                                    Filtered to show only books with readable text content
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Typography variant="body2">Results per page:</Typography>
                                            <TextField
                                                select
                                                size="small"
                                                value={pageSize}
                                                onChange={handlePageSizeChange}
                                                sx={{ minWidth: 80 }}
                                                SelectProps={{
                                                    native: true,
                                                }}
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </TextField>
                                        </Box>
                                    </Box>
                                    
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        {searchResults.map((book, index) => (
                                            <Card 
                                                key={index}
                                                sx={{ 
                                                    cursor: "pointer", 
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:hover": {
                                                        transform: "translateY(-1px)",
                                                        boxShadow: 2
                                                    }
                                                }}
                                                onClick={() => navigate(`/admin/system/integrate/openlibrary/detail${book.key}`)}
                                            >
                                                <CardContent>
                                                    <Box sx={{ display: "flex", gap: 2 }}>
                                                        <CardMedia
                                                            component="img"
                                                            sx={{ width: 80, height: 120, objectFit: "cover" }}
                                                            image={OpenLibraryService.getBookCover(book.cover_i)}
                                                            alt={book.title}
                                                        />
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="h6" component="h3" gutterBottom>
                                                                {book.title}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                                                                {formatAuthors(book.author_name)}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                Published: {formatPublishYear(book.first_publish_year)}
                                                            </Typography>
                                                            {book.publisher && book.publisher.length > 0 && (
                                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                    Publisher: {Array.isArray(book.publisher) ? book.publisher[0] : book.publisher}
                                                                </Typography>
                                                            )}
                                                            {book.subject && book.subject.length > 0 && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                        Subjects:
                                                                    </Typography>
                                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                                        {formatSubjects(book.subject).map((subject, subIndex) => (
                                                                            <Chip
                                                                                key={subIndex}
                                                                                label={subject}
                                                                                size="small"
                                                                                variant="outlined"
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                            {filterTextContent && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Chip 
                                                                        label="📖 Text Available" 
                                                                        size="small" 
                                                                        color="success" 
                                                                        variant="outlined"
                                                                    />
                                                                </Box>
                                                            )}
                                                            {book.availability && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    {book.availability.status === 'full access' && (
                                                                        <Chip 
                                                                            label="📖 Full Access" 
                                                                            size="small" 
                                                                            color="success" 
                                                                            variant="outlined"
                                                                        />
                                                                    )}
                                                                    {book.availability.available_to_browse && (
                                                                        <Chip 
                                                                            label="👁️ Browse Available" 
                                                                            size="small" 
                                                                            color="info" 
                                                                            variant="outlined"
                                                                            sx={{ ml: 0.5 }}
                                                                        />
                                                                    )}
                                                                    {book.availability.available_to_borrow && (
                                                                        <Chip 
                                                                            label="📚 Borrowable" 
                                                                            size="small" 
                                                                            color="warning" 
                                                                            variant="outlined"
                                                                            sx={{ ml: 0.5 }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>

                                    {/* Pagination */}
                                    {searchData && (
                                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                                            <Pagination
                                                count={Math.ceil(Number(totalResults) / Number(pageSize))}
                                                page={Number(currentPage)}
                                                onChange={handlePageChange}
                                                color="primary"
                                                disabled={loading}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default OpenLibraryMain;
